import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Stack, Paper, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SendIcon from '@mui/icons-material/Send';
import BlockchainNetwork from './BlockchainNetwork';

const SectionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden'
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1
}));

const ContactForm = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.default
}));

const SocialCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  textAlign: 'center',
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
    borderColor: theme.palette.primary.main
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: 'white',
  margin: '0 auto 1rem'
}));

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 5000);
    setFormData({ name: '', email: '', message: '' });
  };

  const socialLinks = [
    {
      icon: <EmailIcon fontSize="large" />,
      title: 'Email',
      description: 'mert@example.com',
      action: () => window.open('mailto:mert@example.com')
    },
    {
      icon: <GitHubIcon fontSize="large" />,
      title: 'GitHub',
      description: '@mertdalkir',
      action: () => window.open('https://github.com/mertdalkir')
    },
    {
      icon: <LinkedInIcon fontSize="large" />,
      title: 'LinkedIn',
      description: 'Mert Dalkır',
      action: () => window.open('https://linkedin.com/in/mertdalkir')
    }
  ];

  return (
    <SectionContainer>
      <BlockchainNetwork />
      
      <ContentWrapper>
        <Box sx={{ maxWidth: '1200px', mx: 'auto', px: 4 }}>
        <Typography variant="h2" textAlign="center" gutterBottom>
          İletişime Geçin
        </Typography>
        <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: '600px', mx: 'auto' }}>
          Projeler, iş birlikleri veya herhangi bir konuda benimle iletişime geçmek için aşağıdaki formu kullanabilirsiniz.
        </Typography>
        
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={6}>
          {/* Contact Form */}
          <Box sx={{ flex: 2 }}>
            <ContactForm elevation={0}>
              <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                Mesaj Gönderin
              </Typography>
              
              {showSuccess && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağım.
                </Alert>
              )}
              
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Adınız"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: '12px' 
                      } 
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Email Adresiniz"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: '12px' 
                      } 
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Mesajınız"
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: '12px' 
                      } 
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={<SendIcon />}
                    sx={{
                      borderRadius: '50px',
                      padding: '12px 32px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      alignSelf: 'flex-start'
                    }}
                  >
                    Mesaj Gönder
                  </Button>
                </Stack>
              </form>
            </ContactForm>
          </Box>
          
          {/* Social Links */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Sosyal Medya
            </Typography>
            <Stack spacing={3}>
              {socialLinks.map((link, index) => (
                <SocialCard key={index} elevation={0} onClick={link.action}>
                  <IconWrapper>
                    {link.icon}
                  </IconWrapper>
                  <Typography variant="h6" gutterBottom>
                    {link.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {link.description}
                  </Typography>
                </SocialCard>
              ))}
            </Stack>
          </Box>
        </Stack>
        </Box>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default ContactSection;